import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

/**
 * Reuses an activated route only when its config *and* all of its params are
 * unchanged.
 *
 * This preserves the behaviour the app had under Ionic's `IonicRouteStrategy`
 * (removed in LIESEL-5719). Angular's `DefaultRouteReuseStrategy` compares only
 * `routeConfig`, so navigating `/residents/1` -> `/residents/2` would reuse the
 * component and skip `ngOnInit`. Pages that read their params imperatively -
 * `residents.component.ts` does `this.route.snapshot.params` once in `ngOnInit`
 * - would then keep displaying the previously loaded record.
 *
 * Prefer subscribing to `route.params` in new code; this strategy exists so that
 * removing Ionic did not silently change routing behaviour.
 */
export class ParamAwareRouteReuseStrategy implements RouteReuseStrategy {
  /** Never detach a route for later reuse. */
  shouldDetach(_route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  /** Never reattach a route (nothing is ever stored). */
  shouldAttach(_route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  /** No-op: routes are never detached, so there is nothing to store. */
  store(
    _route: ActivatedRouteSnapshot,
    _detachedTree: DetachedRouteHandle
  ): void {
    return;
  }

  /** Always `null`: no routes are stored for reuse. */
  retrieve(_route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return null;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    if (future.routeConfig !== curr.routeConfig) {
      return false;
    }

    const futureParams = future.params;
    const currentParams = curr.params;
    const futureKeys = Object.keys(futureParams);

    if (futureKeys.length !== Object.keys(currentParams).length) {
      return false;
    }

    return futureKeys.every(key => currentParams[key] === futureParams[key]);
  }
}
